import { Button, Flex, Stack, Td, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiInbox } from "react-icons/fi";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { NeedWaitingListDto, useGetMyNeedsWaitingQuery } from "../../../autogen/bff-api";
import { useDisplayDate } from "../../../common/formatting/useDisplayDate";
import { WIDE_ONLY_CELL } from "../../../common/responsiveTable";
import { urls } from "../../../urls";
import { PanelBody, PanelCard, PanelFigure, PanelHeader } from "./HomePanel";
import { MutedCell, PanelRow, PanelTable, TitleCell } from "./PanelTable";

export const QUEUE_LINK = `${urls.app.needs.list}?waiting=${NeedWaitingListDto.Queue}&order=oldest`;
export const MINE_LINK = `${urls.app.needs.list}?waiting=${NeedWaitingListDto.Mine}`;

export const NeedsWaitingPanel = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const displayDate = useDisplayDate();
  const { data, isLoading, isError } = useGetMyNeedsWaitingQuery({});
  if (data && !data.canTakeNeeds) return <></>;
  const queueCount = data?.queueCount ?? 0;
  const mine = data?.mine ?? [];
  const mineCount = data?.mineCount ?? 0;

  return (
    <PanelCard>
      <PanelHeader titleKey="Needs in the queue" icon={FiInbox} seeAllTo={QUEUE_LINK} />
      <PanelBody isLoading={false} isError={isError} isEmpty={false} emptyKey="No needs are waiting.">
        <Stack spacing="0">
          <PanelFigure
            value={queueCount}
            label={queueCount > 0 ? t("Needs nobody has taken") : t("No needs are waiting.")}
            qualifier={
              queueCount > 0 ? t("The oldest has waited {{count}} days", { count: data?.oldestWaitDays ?? 0 }) : undefined
            }
            isLoading={isLoading}
            divider="bottom"
            onClick={() => navigate(QUEUE_LINK)}
            testId="needs-queue"
          />
          <Text px="4" pt="3" pb="1" fontSize="sm" fontWeight="semibold" color="smText">
            {t("With you")}
          </Text>
          {mine.length === 0 ? (
            <Text px="4" pb="3" fontSize="sm" color="smMuted">
              {t("No needs of yours are waiting for a procurement.")}
            </Text>
          ) : (
            <>
              <PanelTable headers={[t("Title"), t("Desired delivery")]} phoneColumns={1}>
                {mine.map((need) => {
                  const deliveryDate = need.deliveryDate ? displayDate(need.deliveryDate) : undefined;
                  return (
                    <PanelRow key={need.id} to={urls.app.needs.view.go(need.id)}>
                      <TitleCell
                        title={need.title || t("Untitled")}
                        phoneDetail={
                          deliveryDate && (
                            <Flex wrap="wrap" columnGap="2" fontSize="xs" color={need.deliveryPassed ? "smError" : "smMuted"}>
                              <Text>{deliveryDate}</Text>
                              {need.deliveryPassed && <Text>{t("Desired delivery passed")}</Text>}
                            </Flex>
                          )
                        }
                      />
                      {need.deliveryPassed ? (
                        <Td display={WIDE_ONLY_CELL}>
                          <Text fontSize="sm" color="smError">
                            {deliveryDate ?? ""}
                          </Text>
                          <Text fontSize="xs" color="smError">
                            {t("Desired delivery passed")}
                          </Text>
                        </Td>
                      ) : (
                        <MutedCell display={WIDE_ONLY_CELL}>{deliveryDate ?? "—"}</MutedCell>
                      )}
                    </PanelRow>
                  );
                })}
              </PanelTable>
              {mineCount > mine.length && (
                <Text px="4" py="3" fontSize="sm">
                  <Button as={RouterLink} to={MINE_LINK} variant="link" size="sm" colorScheme="teal">
                    {t("See all {{count}} with you", { count: mineCount })}
                  </Button>
                </Text>
              )}
            </>
          )}
        </Stack>
      </PanelBody>
    </PanelCard>
  );
};
